import { useCallback, useLayoutEffect, useRef } from 'react';

const FLIP_EASING = 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)';

export const useHorizontalFlip = (animationKey: string, enabled = true) => {
  const nodesRef = useRef(new Map<string, HTMLElement>());
  const rafRef = useRef<number[]>([]);

  const register = useCallback((id: string) => (element: HTMLElement | null) => {
    if (element) {
      nodesRef.current.set(id, element);
      return;
    }

    nodesRef.current.delete(id);
  }, []);

  useLayoutEffect(() => {
    rafRef.current.forEach((frameId) => window.cancelAnimationFrame(frameId));
    rafRef.current = [];

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!enabled || prefersReducedMotion) {
      nodesRef.current.forEach((element) => {
        element.style.transition = '';
        element.style.transform = '';
      });
      return;
    }

    const visualLeftById = new Map<string, number>();

    nodesRef.current.forEach((element, id) => {
      visualLeftById.set(id, element.getBoundingClientRect().left);
    });

    nodesRef.current.forEach((element) => {
      element.style.transition = 'none';
      element.style.transform = '';
    });

    const movingElements: HTMLElement[] = [];

    nodesRef.current.forEach((element, id) => {
      const layoutLeft = element.getBoundingClientRect().left;
      const visualLeft = visualLeftById.get(id) ?? layoutLeft;
      const delta = visualLeft - layoutLeft;

      if (Math.abs(delta) < 1) {
        return;
      }

      element.style.transform = `translateX(${delta}px)`;
      movingElements.push(element);
    });

    if (movingElements.length === 0) {
      return;
    }

    const playFrame = window.requestAnimationFrame(() => {
      const commitFrame = window.requestAnimationFrame(() => {
        movingElements.forEach((element) => {
          element.style.transition = FLIP_EASING;
          element.style.transform = '';
        });
      });

      rafRef.current.push(commitFrame);
    });

    rafRef.current.push(playFrame);

    return () => {
      rafRef.current.forEach((frameId) => window.cancelAnimationFrame(frameId));
      rafRef.current = [];
    };
  }, [animationKey, enabled]);

  return register;
};
